/* eslint-disable @typescript-eslint/no-explicit-any */
import { ProcessServerConfigFunction } from 'filepond';
import { html, unsafeCSS } from 'lit';
import { classMap } from 'lit/directives/class-map.js';
import { ifDefined } from 'lit/directives/if-defined.js';
import { property } from 'lit/decorators.js';
import { type UploadedFile } from './filepond-wrapper.wc';
// eslint-disable-next-line no-duplicate-imports
import './filepond-wrapper.wc';

import { EntityTypes } from './filepond.types';
import { BaseElement, customElement } from '../../base-element';
import { Translate } from '../../base-element/mixins/translation-mixin';
import styles from './filepond.scss?inline';

export interface FileuploadProps {
  entityType?: EntityTypes;
  pictureSrc?: string;
  name?: string;
  readonly?: boolean;
  type?: 'image' | 'file';
  previewLabel?: string;
  previewCta?: string;
  accept?: string[];
  label?: string;
  labelFileProcessingComplete?: string;
  required?: boolean;
  allowMultiple?: boolean;
  vertical?: boolean;
  onChange?: any;
  onUpdate: any;
  onRemove: any;
  files: UploadedFile[];
  processFunction: ProcessServerConfigFunction;
}

const renderFilepondLabel = ({
  vertical,
  pictureSrc,
  label,
  previewCta,
}: {
  vertical?: boolean;
  pictureSrc?: string;
  previewCta?: string;
  label?: string;
}) => `
    <div class="${`c-fp-upload__preview ${vertical ? 'c-fp-upload__preview--vertical' : ''}`}">
      <div class="c-fp-upload__preview-icon">
        ${
          pictureSrc
            ? `<ps-avatar size="2xlarge" src=${pictureSrc}></ps-avatar>`
            : `<ps-icon name="arrow-up" weight="bold" size="small"></ps-icon>`
        }
      </div>
      <div class="c-fp-upload__preview-content">
        <p class="c-fp-upload__preview-text">
          ${label}
        </p>
        <p class="c-fp-upload__preview-info">
          ${previewCta}
        </span></p>
      </div>
      ${
        pictureSrc
          ? `
            <button type="button" class="c-fp-upload__preview-remove">
              <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" width="24" height="24">
                <path d="m5 19 7-7m7-7-7 7m0 0 7 7L5 5" stroke="currentColor" stroke-width="1"></path>
              </svg>
            </button>
          `
          : ''
      }
    </div>
   `;

@customElement('ps-fileupload')
export class FileuploadWC
  extends Translate(BaseElement)
  implements FileuploadProps
{
  static styles = unsafeCSS(styles);

  @property({ type: Array }) accept?: FileuploadProps['accept'] = [];

  @property({ reflect: true, attribute: 'entity-type' })
  entityType?: FileuploadProps['entityType'];

  @property({ attribute: 'picture-src' })
  pictureSrc?: FileuploadProps['pictureSrc'];

  @property({ reflect: true }) label: FileuploadProps['label'] = '';

  @property({ reflect: true })
  labelFileProcessingComplete: FileuploadProps['labelFileProcessingComplete'] =
    'Success, file uploaded!';

  @property() name?: FileuploadProps['name'];

  @property({ reflect: true, attribute: 'preview-label' })
  previewLabel: FileuploadProps['previewLabel'] = 'Drag a photo here';

  @property({ reflect: true, attribute: 'preview-cta' })
  previewCta: FileuploadProps['previewCta'] = 'Select a file';

  @property({ reflect: true }) type?: FileuploadProps['type'] = 'image';

  @property({ type: Boolean }) readonly?: FileuploadProps['readonly'];

  @property({ type: Boolean, attribute: 'allow-multiple' })
  allowMultiple?: FileuploadProps['allowMultiple'];

  @property({ type: Boolean }) required?: FileuploadProps['required'];

  @property({ type: Boolean, reflect: true })
  vertical?: FileuploadProps['vertical'];

  @property({ type: Object })
  onUpload: FileuploadProps['onChange'];

  @property() onChange: FileuploadProps['onChange'];

  @property() onRemove: FileuploadProps['onRemove'];

  @property() onUpdate: FileuploadProps['onUpdate'];

  @property()
  processFunction: FileuploadProps['processFunction'];

  @property({ type: Array }) files: FileuploadProps['files'] = [];

  render() {
    if (this.readonly)
      return html`
          <div
            class=${classMap({
              'c-fp-upload--new': true,
              'c-fp-upload': true,
              'c-fp-upload--vertical': this.vertical ?? false,
              'c-fp-upload--readonly': true,
              'c-fp-upload--readonly-has-image': !!this.pictureSrc,
              [`c-fp-upload--${this.entityType?.toLowerCase()}`]:
                !!this.entityType,
            })}
          >
            <div class=${classMap({
              'c-fp-upload__preview': true,
              'c-fp-upload__preview--vertical': this.vertical ?? false,
            })}>
              <div class="c-fp-upload__preview-icon">
                ${
                  this.pictureSrc
                    ? html`<ps-avatar
                        size="2xlarge"
                        src=${this.pictureSrc}
                      ></ps-avatar>`
                    : html`<ps-icon
                        name="arrow-up"
                        weight="bold"
                        size="small"
                      ></ps-icon>`
                }
              </div>
              <div class="c-fp-upload__preview-content">
                <p class="c-fp-upload__preview-text">
                  ${this.previewLabel}
                </p>
                <p class="c-fp-upload__preview-info">
                  ${this.previewCta}
                </span></p>
              </div>
            </div>
          </div>
      `;

    return html`
      <div
        class=${`c-fp-upload c-fp-upload--${this.entityType?.toLowerCase()} ${this.vertical ? 'c-fp-upload--vertical' : ''}`}
      >
        ${this.label
          ? html` <p class="c-fp-upload__label-text">${this.label}</p> `
          : ''}
        <ps-filepond-wrapper
          .clearAfterUpload=${this.allowMultiple}
          .acceptedFileTypes=${this.accept}
          ?disabled=${this.readonly}
          .files=${this.files}
          type=${ifDefined(this.type)}
          name=${ifDefined(this.name)}
          .labelIdle=${renderFilepondLabel({
            vertical: this.vertical ?? false,
            pictureSrc: this.pictureSrc ?? '',
            label: this.pictureSrc
              ? this.labelFileProcessingComplete
              : this.previewLabel,
            previewCta: this.previewCta ?? '',
          })}
          labelFileProcessing="Uploading..."
          labelFileProcessingComplete=${ifDefined(
            this.labelFileProcessingComplete
          )}
          labelFileProcessingError=${() => 'My server errored'}
          .onremovefile=${this.onRemove}
          .onupdatefiles=${this.onUpdate}
          .server=${{
            revert: (uniqueFileId: string, load: () => void) => {
              this.files = [];
              // Should call the load method when done, no parameters required
              load();
            },
            process: this.processFunction,
          }}
        ></ps-filepond-wrapper>
      </div>
    `;
  }
}

declare global {
  interface HTMLElementTagNameMap {
    'ps-fileupload': FileuploadWC;
  }
  enum PSElementTagNameMap {
    'ps-fileupload' = 'ps-fileupload',
  }
}
